import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { useGetDealerDropdownListQuery } from '../../api/endpoints/dealer';
import { useLanguage } from '../../contexts/LanguageContext';

const DealerDropdown = ({ value, onChange, className = '', disabled = false, rowCount = 10, multiple = false, showSelectAll = true }) => {
    const [selectedDealer, setSelectedDealer] = useState(multiple ? [] : null);
    const [searchText, setSearchText] = useState('');
    const [dropdownKey, setDropdownKey] = useState(0);
    const { getCurrentLanguageId, th } = useLanguage();

    const { data: dealerList = [], isLoading: isLoadingDealers } = useGetDealerDropdownListQuery({
        RowCount: rowCount || 50,
        SearchText: searchText || (multiple ? '' : value)
    });

    const dealerOptions = useMemo(
        () =>
            dealerList.map((dealer) => ({
                id: dealer.dealerId,
                name: dealer.name,
                text: dealer.translatedText || dealer.name,
                erpDealerId: dealer.dealerId,
                displayValue: dealer.name
            })),
        [dealerList]
    );

    const selectAllOption = useMemo(
        () => ({
            id: 'select-all',
            name: th('dropdown.dealer.selectAll'),
            displayValue: th('dropdown.dealer.selectAll'),
            isSelectAll: true
        }),
        [th]
    );

    const mergedDealerOptions = useMemo(() => {
        const base = [...dealerOptions];
        const addIfMissing = (opt) => {
            if (!opt) return;
            const exists = base.some((x) => x.id?.toString() === opt.id?.toString());
            if (!exists) base.unshift(opt); // seçiliyi başa ekle
        };

        if (multiple) {
            (selectedDealer || []).forEach(addIfMissing);
        } else {
            addIfMissing(selectedDealer);
        }

        // uniq garanti (id bazlı)
        const uniqMap = new Map();
        base.forEach((o) => uniqMap.set(o.id?.toString(), o));
        return Array.from(uniqMap.values());
    }, [dealerOptions, selectedDealer, multiple]);

    const enhancedOptions = useMemo(() => (multiple && showSelectAll ? [selectAllOption, ...mergedDealerOptions] : mergedDealerOptions), [multiple, showSelectAll, selectAllOption, mergedDealerOptions]);

    const handleSelectionChange = (selectedValue) => {
        if (multiple) {
            const currentSelection = Array.isArray(selectedValue) ? selectedValue : [];
            const selectAllClicked = currentSelection.some((opt) => opt?.isSelectAll);

            if (selectAllClicked) {
                const areAllSelected = selectedDealer && selectedDealer.length === dealerOptions.length;

                if (areAllSelected) {
                    setSelectedDealer([]);
                    onChange({ isAllDealers: false, dealerIds: [] });
                } else {
                    setSelectedDealer([...dealerOptions]);

                    onChange({ isAllDealers: true, dealerIds: [] });
                }
                return;
            }

            const finalSelection = currentSelection.filter((opt) => !opt?.isSelectAll);
            setSelectedDealer(finalSelection);
            onChange({ isAllDealers: false, dealerIds: finalSelection.map((opt) => opt.id) });
            return;
        }

        setSelectedDealer(selectedValue);
        onChange({ isAllDealers: false, dealerIds: selectedValue ? [selectedValue.id] : [] });
    };

    const handleFilterChange = (e) => {
        setSearchText(e.filter);
    };

    const handleDropdownHide = () => {
        setSearchText('');
        setDropdownKey((prev) => prev + 1);
    };

    const itemTemplate = (option) => {
        if (!multiple) {
            return <div>{option.text}</div>;
        }

        const isSelected = selectedDealer && selectedDealer.some((sel) => sel.id === option.id);
        const isSelectAllOption = option.isSelectAll;
        const areAllSelected = selectedDealer && selectedDealer.length === dealerOptions.length;

        return (
            <div className="flex align-items-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={isSelectAllOption ? areAllSelected : isSelected}
                    className="mr-2"
                    onChange={(e) => {
                        if (isSelectAllOption) {
                            if (areAllSelected) {
                                setSelectedDealer([]);
                                onChange({ isAllDealers: false, dealerIds: [] });
                            } else {
                                setSelectedDealer([...dealerOptions]);
                                onChange({ isAllDealers: true, dealerIds: [] });
                            }
                            return;
                        } else {
                            const newSelection = [...(selectedDealer || [])];
                            const optionIndex = newSelection.findIndex((sel) => sel.id === option.id);

                            if (e.checked) {
                                if (optionIndex === -1) {
                                    newSelection.push(option);
                                }
                            } else {
                                if (optionIndex > -1) {
                                    newSelection.splice(optionIndex, 1);
                                }
                            }

                            handleSelectionChange(newSelection);
                        }
                    }}
                />
                {option.isSelectAll ? <span style={{ fontWeight: 'bold' }}>{option.displayValue}</span> : <span>{option.text}</span>}
            </div>
        );
    };

    const valueTemplate = (option, props) => {
        if (!multiple) {
            return option ? option.displayValue : props.placeholder;
        }

        if (!selectedDealer || selectedDealer.length === 0) {
            return props.placeholder;
        }

        if (selectedDealer.length === dealerOptions.length) {
            return th('dropdown.dealer.allSelected');
        }

        if (selectedDealer.length === 1) {
            return selectedDealer[0].displayValue;
        }
        return th('dropdown.dealer.dealersSelected', { count: selectedDealer.length });
    };

    useEffect(() => {
        if (!value) {
            setSelectedDealer(multiple ? [] : null);
            return;
        }

        // dealerOptions boşsa (api henüz gelmediyse) state'i bozma
        if (dealerOptions.length === 0) return;

        if (multiple) {
            if (value.isAllDealers) {
                setSelectedDealer(dealerOptions);
                return;
            }

            const ids = Array.isArray(value.dealerIds) ? value.dealerIds : [];

            // bulunanları al, bulunamayanları ise mevcut selected'dan koru
            setSelectedDealer((prev) => {
                const prevArr = Array.isArray(prev) ? prev : [];

                const next = ids.map((id) => dealerOptions.find((opt) => opt.id?.toString() === id?.toString()) || prevArr.find((p) => p.id?.toString() === id?.toString())).filter(Boolean);

                return next;
            });

            return;
        }

        // single
        const firstId = Array.isArray(value.dealerIds) ? value.dealerIds[0] : null;

        setSelectedDealer((prev) => {
            const found = dealerOptions.find((opt) => opt.id?.toString() === firstId?.toString());

            // found yoksa prev'i KORU (null'a düşme)
            if (!found) return prev;

            return found;
        });
    }, [value, dealerOptions, multiple]);

    return (
        <Dropdown
            key={dropdownKey}
            value={selectedDealer}
            options={enhancedOptions}
            onChange={(e) => handleSelectionChange(e.value)}
            onFilter={handleFilterChange}
            onHide={handleDropdownHide}
            optionLabel="displayValue"
            filter
            showClear
            filterBy="name,text"
            className={className}
            placeholder={th('dropdown.dealer.placeholder')}
            loading={isLoadingDealers}
            disabled={disabled}
            virtualScrollerOptions={{ itemSize: 38 }}
            multiple={multiple}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            appendTo={typeof document !== 'undefined' ? document.body : 'self'}
            filterLocale="tr"
        />
    );
};

export default DealerDropdown;
